import React, { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import { Icon, theme, useHovered, webOnlyStyles } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsCarouselNav = (props: Props) => {
  const { hasPrev, hasNext = true, onPrevPress, onNextPress } = props;

  const { formatMessage } = useIntl();

  return (
    <View style={styles.container} pointerEvents="box-none">
      <ProductDetailsCarouselNavButton
        variation="prev"
        isDisabled={!hasPrev}
        onPress={onPrevPress}
        accessibilityLabel={formatMessage(messages.prevSlideBtnA11yLabel)}
      />

      <ProductDetailsCarouselNavButton
        variation="next"
        isDisabled={!hasNext}
        onPress={onNextPress}
        accessibilityLabel={formatMessage(messages.nextSlideBtnA11yLabel)}
      />
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsCarouselNavButton = (props: ButtonProps) => {
  const { onPress, variation, accessibilityLabel, isDisabled } = props;

  // ─────────────────────────────────────────────────────────────────────

  const ref = useRef<View>(null);
  const isHovered = useHovered(ref);

  // ─── Styles ──────────────────────────────────────────────────────────

  const buttonStyles = [
    styles.navBtn,
    variation === 'prev' ? styles.navBtnPrev : styles.navBtnNext,
    isDisabled ? styles.navBtnDisabled : undefined,
    isHovered && !isDisabled ? styles.navBtnHover : undefined,
    navBtnWebStyles,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={ref}
      onPress={onPress}
      style={buttonStyles}
      disabled={isDisabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
    >
      <Icon
        name={variation === 'prev' ? 'IconCaretLeft' : 'IconCaretRight'}
        width={NAV_BTN_ICON_SIZE}
        height={NAV_BTN_ICON_SIZE}
        color={NAV_BTN_ICON_COLOR}
      />
    </Pressable>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const NAV_BTN_SIZE = 60;
const NAV_BTN_ICON_SIZE = 42;
const NAV_BTN_ICON_COLOR = theme.colors.CHARCOAL;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  prevSlideBtnA11yLabel: {
    defaultMessage: 'Go to previous slide',
    description: 'Product Details | Carousel | Previous slide button | a11y label',
  },
  nextSlideBtnA11yLabel: {
    defaultMessage: 'Go to next slide',
    description: 'Product Details | Carousel | Next slide button | a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navBtn: {
    position: 'absolute',
    zIndex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: NAV_BTN_SIZE,
    height: NAV_BTN_SIZE,
    borderRadius: NAV_BTN_SIZE,
    borderWidth: 1,
    borderColor: theme.colors.LIGHT,
    backgroundColor: theme.colors.CREAM,
  },
  navBtnHover: {
    backgroundColor: theme.colors.LINEN,
  },
  navBtnDisabled: {
    opacity: 0.3,
  },
  navBtnPrev: {
    left: theme.spacing['6'],
  },
  navBtnNext: {
    right: theme.spacing['6'],
  },
});

const navBtnWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: `background-color ${theme.transitions.base}ms, opacity ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Readonly<{
  hasPrev: boolean;
  hasNext?: boolean;
  onPrevPress: () => void;
  onNextPress: () => void;
}>;

type ButtonProps = {
  variation: 'prev' | 'next';
  accessibilityLabel: string;
  isDisabled?: boolean;
  onPress: () => void;
};
